import { useState } from 'react'
import { FiMapPin, FiMail, FiSend } from 'react-icons/fi'
import toast from 'react-hot-toast'
import Card from './ui/Card'
import SectionTitle from './ui/SectionTitle'
import Button from './ui/Button'
import Select from './ui/Select'
import Reveal from './ui/Reveal'
import LegalConsentText from './LegalConsentText'
import { LEGAL } from '../config/legal'

const SUPPORT_EMAIL = LEGAL.privacyEmail
const FORM_SUBMIT_URL = `https://formsubmit.co/ajax/${encodeURIComponent(SUPPORT_EMAIL)}`

const TYPE_OPTIONS = [
  { value: '', label: 'Selecione o tipo' },
  { value: 'SUPPORT', label: 'Suporte' },
  { value: 'FEEDBACK', label: 'Feedback' },
  { value: 'OTHER', label: 'Outros' },
]

const TYPE_LABELS = {
  SUPPORT: 'Suporte',
  FEEDBACK: 'Feedback',
  OTHER: 'Outros',
}

const EMPTY_FORM = {
  nome: '',
  email: '',
  tipo: '',
  assunto: '',
  mensagem: '',
}

const FIELD = 'input-field !min-h-[42px] !py-2.5 !px-4 !text-sm'
const TEXTAREA = 'textarea-field !py-2.5 !px-4 !text-sm'

const Contact = () => {
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!acceptedPrivacy) {
      toast.error('Aceite o tratamento dos dados conforme a Política de Privacidade.')
      return
    }
    setLoading(true)
    setSubmitSuccess(false)

    const tipoLabel = TYPE_LABELS[formData.tipo] || formData.tipo
    const mailSubject = `[Suporte] [${formData.tipo}] ${formData.assunto.trim()} - ${formData.nome.trim()}`

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          _subject: mailSubject,
          _replyto: formData.email.trim(),
          _template: 'table',
          _captcha: 'false',
          Nome: formData.nome.trim(),
          Email: formData.email.trim(),
          Tipo: `${tipoLabel} (${formData.tipo})`,
          Assunto: formData.assunto.trim(),
          Mensagem: formData.mensagem.trim(),
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok && data?.success) {
        toast.success('Mensagem enviada com sucesso!')
        setSubmitSuccess(true)
        setFormData(EMPTY_FORM)
      } else {
        throw new Error(data?.message || 'Erro ao enviar mensagem')
      }
    } catch (error) {
      console.error('FormSubmit:', error)
      toast.error(`Erro ao enviar. Escreva para ${SUPPORT_EMAIL}`)
    } finally {
      setLoading(false)
    }
  }

  const contactInfo = [
    {
      icon: FiMapPin,
      label: 'Localização',
      value: 'Pau dos Ferros, RN',
    },
    {
      icon: FiMail,
      label: 'E-mail',
      value: SUPPORT_EMAIL,
      href: `mailto:${SUPPORT_EMAIL}`,
    },
  ]

  return (
    <section id="contact" className="section-padding section-alt">
      <div className="container-custom">
        <Reveal direction="up">
          <SectionTitle
            title="Entre em Contato"
            subtitle="Fale Conosco"
            align="center"
          />
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 w-full min-w-0">
          <Reveal direction="right">
            <Card padding="lg" className="relative overflow-hidden !p-5 sm:!p-6 lg:!p-7">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/60 via-accent to-accent/60" aria-hidden="true" />
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="nome" className="form-label">
                    Nome
                  </label>
                  <input
                    id="nome"
                    type="text"
                    required
                    maxLength={120}
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className={FIELD}
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="form-label">
                    E-mail
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none flex-shrink-0" />
                    <input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`${FIELD} !pl-10`}
                      placeholder="seu@email.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="tipo" className="form-label">
                    Tipo
                  </label>
                  <Select
                    id="tipo"
                    name="tipo"
                    options={TYPE_OPTIONS}
                    value={formData.tipo}
                    onChange={(val) => setFormData({ ...formData, tipo: val })}
                    placeholder="Selecione o tipo"
                    required
                    compact
                    aria-label="Tipo de mensagem"
                  />
                </div>

                <div>
                  <label htmlFor="assunto" className="form-label">
                    Assunto
                  </label>
                  <input
                    id="assunto"
                    type="text"
                    required
                    maxLength={200}
                    value={formData.assunto}
                    onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                    className={FIELD}
                    placeholder="Resumo da sua solicitação"
                  />
                </div>

                <div>
                  <label htmlFor="mensagem" className="form-label">
                    Mensagem
                  </label>
                  <textarea
                    id="mensagem"
                    required
                    maxLength={5000}
                    value={formData.mensagem}
                    onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                    placeholder="Descreva sua dúvida ou solicitação..."
                    rows={5}
                    className={TEXTAREA}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent"
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    <LegalConsentText /> Autorizo o envio dos dados informados para atendimento via
                    FormSubmit, que encaminhará a mensagem ao e-mail {SUPPORT_EMAIL}.
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  size="sm"
                  className="w-full !min-h-[36px] !py-2 !px-4 !text-sm !rounded-xl active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <FiSend className="w-4 h-4 mr-2" />
                      Enviar Mensagem
                    </>
                  )}
                </Button>
                {submitSuccess && (
                  <p className="text-center text-accent font-semibold text-sm sm:text-base flex items-center justify-center gap-2 animate-fade-in" role="status">
                    <span className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                      <FiSend className="w-3 h-3 text-accent" />
                    </span>
                    Mensagem recebida! Responderemos em breve.
                  </p>
                )}
              </form>
            </Card>
          </Reveal>

          <Reveal direction="left" delay={200}>
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Informações de Contato
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                  Envie suporte, feedback ou outras solicitações pelo formulário. As mensagens chegam em{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-accent hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon
                  const Component = info.href ? 'a' : 'div'
                  return (
                    <Reveal key={index} direction="up" delay={index * 100}>
                      <Card hover padding="md" className="group !p-5">
                        <Component
                          href={info.href}
                          className="flex items-start gap-4"
                        >
                          <div className="w-12 h-12 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                            <Icon className="w-6 h-6 text-accent group-hover:text-white transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                              {info.label}
                            </p>
                            <p className="text-base font-semibold text-gray-900 dark:text-white break-all">
                              {info.value}
                            </p>
                          </div>
                        </Component>
                      </Card>
                    </Reveal>
                  )
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export default Contact
